import "@babel/polyfill";
import registerServiceWorker from "./registerServiceWorker";
import "reflect-metadata";

import * as React from "react";
import CoreApp, {
    ApplicationGuardEvent,
    ConfigHelper,
    IApplicationGuard,
    IApplicationUser,
    ThemeVars,
    TypeDefs as CoreTypeDefs
} from "@athena-portal/core";

import pluginJourney from "@athena-portal/plugin-journey";
import pluginKeycloak from "@athena-portal/plugin-keycloak";
import pluginDashboard from "./config/dashboardPlugin";
import customerPortalPlugin from "./config/customerPortalPlugin";
import pluginFalconJourney from "@falcon/plugin-falcon-journey";
import {ApmRumLoggerServiceImpl, ILoggerService, Types} from "@falcon/services";

// Import locales
import * as lv from "react-intl/locale-data/lv";
import * as en from "react-intl/locale-data/en";

// Import theme
import {selectTheme} from "./ThemeUtils";
import {Layout} from "./Layout";

// APM RUM
import {ApmBase, init as initApm} from "@elastic/apm-rum";

let apm: ApmBase | undefined;
if (process.env.REACT_APP_APM_SERVER_ENABLED?.toString() === "true") {
    apm = initApm({
        serviceName: process.env.REACT_APP_APPLICATION_NAME,
        serverUrl: process.env.REACT_APP_APM_SERVER_URL,
        distributedTracingOrigins: process.env.REACT_APP_APM_DISTRIBUTED_TRACING_ORIGINS ? process.env.REACT_APP_APM_DISTRIBUTED_TRACING_ORIGINS.split(",") : [],
        serviceVersion: process.env.REACT_APP_APPLICATION_VERSION,
        propagateTracestate: true
    })
}

// Create app instance
const app = new CoreApp();
const theme = selectTheme();
app.setTheme(theme as ThemeVars);

app.i18n().setAvailableLocales({
    "en-GB": en,
    "lv-LV": lv
});

app.registerPlugins(
    {
        plugin: pluginJourney,
        configuration: {
            runtimeService: {
                serviceBaseUrl: process.env.REACT_APP_JOURNEY_SERVICE_BASE_URL,
                customHeaders: ConfigHelper.parseMapFromString(process.env.REACT_APP_JOURNEY_SERVICE_CUSTOM_HEADERS ? process.env.REACT_APP_JOURNEY_SERVICE_CUSTOM_HEADERS : ""),
                customQueryStringParameters: ConfigHelper.parseMapFromString(process.env.REACT_APP_JOURNEY_SERVICE_CUSTOM_QUERY_STRING ? process.env.REACT_APP_JOURNEY_SERVICE_CUSTOM_QUERY_STRING : "")
            }
        }
    }, {
        plugin: pluginFalconJourney,
        configuration: {
            falconConvenienceApiService: {
                serviceBaseUrl: process.env.REACT_APP_FALCON_CONVENIENCE_API_BASE_URL,
                customHeaders: ConfigHelper.parseMapFromString(process.env.REACT_APP_FALCON_CONVENIENCE_API_CUSTOM_HEADERS ? process.env.REACT_APP_FALCON_CONVENIENCE_API_CUSTOM_HEADERS : ""),
                customQueryStringParameters: ConfigHelper.parseMapFromString(process.env.REACT_APP_FALCON_CONVENIENCE_API_CUSTOM_QUERY_STRING ? process.env.REACT_APP_FALCON_CONVENIENCE_API_CUSTOM_QUERY_STRING : "")
            }
        }
    }, {
        plugin: pluginKeycloak,
        configuration: {
            realm: process.env.REACT_APP_KEYCLOAK_REALM,
            url: process.env.REACT_APP_KEYCLOAK_URL,
            clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID,
            clientSecret: process.env.REACT_APP_KEYCLOAK_CLIENT_SECRET,
            attributeMapping: [
                (attributes: { [key: string]: string[] }, user: any) => {
                    user.mobile = attributes.mobile ? attributes.mobile[0] : undefined;
                    user.title = attributes.title ? attributes.title[0] : undefined;
                }
            ]
        }
    }, {
        plugin: pluginDashboard,
        configuration: {
            customerPortalApiService: {
                serviceBaseUrl: process.env.REACT_APP_FALCON_CUSTOMER_PORTAL_API_BASE_URL,
                customHeaders: ConfigHelper.parseMapFromString(process.env.REACT_APP_FALCON_CUSTOMER_PORTAL_API_CUSTOM_HEADERS ? process.env.REACT_APP_FALCON_CUSTOMER_PORTAL_API_CUSTOM_HEADERS : ""),
                customQueryStringParameters: ConfigHelper.parseMapFromString(process.env.REACT_APP_FALCON_CUSTOMER_PORTAL_API_CUSTOM_QUERY_STRING ? process.env.REACT_APP_FALCON_CUSTOMER_PORTAL_API_CUSTOM_QUERY_STRING : "")
            }
        }
    }, {
        plugin: customerPortalPlugin
    }
);

const windowObject = window as any;

// Set APM RUM and ContentSquare context
const services = app.getExtensionPoints().services;
const appGuard = services.get<IApplicationGuard>(CoreTypeDefs.ApplicationGuard);
if (appGuard) {
    if (apm) {
        // Set user id to APM RUM context when user logs in on reloads page when session is still on
        appGuard.on(ApplicationGuardEvent.Login, (user: IApplicationUser) => apm!.setUserContext({
            id: user.id
        }));

        // Unset user id from APM RUM context when user logs out
        appGuard.on(ApplicationGuardEvent.Logout, () => apm!.setUserContext({
            id: ''
        }));
    }

    windowObject.addEventListener('load', () => {
        if (typeof (windowObject._uxa) === 'object') {
            if (typeof (windowObject._uxa.push) === 'function') {
                // Set user id to ContentSquare when user logs in on reloads page when session is still on
                appGuard.on(ApplicationGuardEvent.Login, (user: IApplicationUser) => windowObject._uxa.push(['trackPageEvent', '@user-identifier@' + user.id]));

                // Unset user id from ContentSquare when user logs out
                appGuard.on(ApplicationGuardEvent.Logout, () => windowObject._uxa.push(['trackPageEvent', '@user-identifier@' + ""]));
            }
        }
    })
}

if (services.isBound(Types.LoggerService)) {
    // LoggerService is bound in Athena to a dummy logger. Have to unbind it first.
    services.unbind(Types.LoggerService);
}
services.bind<ILoggerService>(Types.LoggerService)
    .toDynamicValue(() => new ApmRumLoggerServiceImpl(apm)).inSingletonScope();

// Render app
app.render(
    <Layout/>,
    document.getElementById("root") as HTMLElement
);

registerServiceWorker();



// WEBPACK FOOTER //
// ./src/index.tsx